import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import accommodationAgeGroupCapacityObject from '@/entities/accommodationAgeGroupCapacity/interface'
import accommodationAgeGroupedTotalCapacityObject from '@/entities/accommodationAgeGroupedTotalCapacity/interface'
import travelPricingAgeGroupObject from '@/entities/travelPricingAgeGroup/interface'

import {
  ALL_CAPACITY_SETTINGS_QUERY,
  SINGLE_CAPACITY_SETTINGS_QUERY,
  CAPACITY_SETTINGS_DEPENDENTS_QUERY,
  CREATE_CAPACITY_SETTINGS_MUTATION,
  UPDATE_CAPACITY_SETTINGS_MUTATION,
  DELETE_CAPACITY_SETTINGS_MUTATION,
  FORCE_DELETE_CAPACITY_SETTINGS_MUTATION,
  ADD_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION,
  REMOVE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION,
  CREATE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION,
  DELETE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION,
  UPDATE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_ORDER_MUTATION,
  ADD_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION,
  REMOVE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION,
  CREATE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION,
  DELETE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION,
  UPDATE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_ORDER_MUTATION,
  ADD_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION,
  REMOVE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION,
  CREATE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION,
  DELETE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION,
  UPDATE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const accommodationCapacitySettingsRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_CAPACITY_SETTINGS_QUERY, variables).then((data: any) => {
      return hydrateObject(data.capacitySettings)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_CAPACITY_SETTINGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.capacitySettings)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_CAPACITY_SETTINGS_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.capacitySettings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_CAPACITY_SETTINGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.capacitySettings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(CAPACITY_SETTINGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([CAPACITY_SETTINGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_CAPACITY_SETTINGS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.capacitySettings)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_CAPACITY_SETTINGS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.capacitySettings)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_CAPACITY_SETTINGS_MUTATION, variables).then((data: any) => {
      return data.deleteAccommodationCapacitySettings
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_CAPACITY_SETTINGS_MUTATION, variables).then((data: any) => {
      return data.forceDeleteAccommodationCapacitySettings
    })
  },

  assignAgeGroupCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION, variables)
  },

  unassignAgeGroupCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION, variables)
  },

  createAgeGroupCapacities: async (id: string, items: Array<accommodationAgeGroupCapacityObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupCapacities: items,
      },
    }
    return graphQLClient.request(CREATE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION, variables)
  },

  deleteAgeGroupCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_MUTATION, variables)
  },

  updateAgeGroupCapacitiesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_CAPACITY_SETTINGS_AGE_GROUP_CAPACITIES_ORDER_MUTATION, variables)
  },

  assignAgeGroupedTotalCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupedTotalCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION, variables)
  },

  unassignAgeGroupedTotalCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupedTotalCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION, variables)
  },

  createAgeGroupedTotalCapacities: async (id: string, items: Array<accommodationAgeGroupedTotalCapacityObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupedTotalCapacities: items,
      },
    }
    return graphQLClient.request(CREATE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION, variables)
  },

  deleteAgeGroupedTotalCapacities: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupedTotalCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_MUTATION, variables)
  },

  updateAgeGroupedTotalCapacitiesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        ageGroupedTotalCapacityIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_CAPACITY_SETTINGS_AGE_GROUPED_TOTAL_CAPACITIES_ORDER_MUTATION, variables)
  },

  assignPartialOccupancyAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partialOccupancyAllowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  unassignPartialOccupancyAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partialOccupancyAllowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  createPartialOccupancyAllowedAgeGroups: async (id: string, items: Array<travelPricingAgeGroupObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partialOccupancyAllowedAgeGroups: items,
      },
    }
    return graphQLClient.request(CREATE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  deletePartialOccupancyAllowedAgeGroups: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partialOccupancyAllowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_MUTATION, variables)
  },

  updatePartialOccupancyAllowedAgeGroupsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partialOccupancyAllowedAgeGroupIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_CAPACITY_SETTINGS_PARTIAL_OCCUPANCY_ALLOWED_AGE_GROUPS_ORDER_MUTATION, variables)
  },
}

export default accommodationCapacitySettingsRepository
